import { resolveEndpoint } from './transport/endpoints';
import { DEFAULT_EXTERNAL_IGNORE_URLS } from './transport/internalHostAllowlist';
import type { BrowserSDKConfig, HyperDXInstance } from './types/hyperdx';
import { LoggerMetrics, loggerMetrics } from './metrics';
import type { Attributes, LogEntry } from './types';
import { sanitizeAttrs } from './utils';
import type { WorkerFactory } from './worker';
import { WorkerClient } from './worker';

/**
 * 로그 레벨. 이전에는 `@zep/proto`의 `ZLogLevel`(string enum)을 직접 import 했으나,
 * proto 는 CommonJS barrel 패키지라 트리셰이킹이 되지 않아 `z_net`/`z_common_data` 전체
 * (~489KB raw)가 진입 번들(`_app.js`)에 통째로 유입됐다. 이 enum 4개 값을 위해 게임
 * 네트워크 프로토콜 전체가 딸려오던 문제를 끊기 위해 로컬로 정의한다.
 * 값은 proto 정의(`z_common_data.proto`)와 반드시 동일하게 유지한다.
 */
export enum ZLogLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  UNRECOGNIZED = 'UNRECOGNIZED',
}

const MAX_QUEUE_LENGTH = 1000;
const MAX_SESSION_RETRIES = 5;
const MAX_INIT_RETRIES = 3;
const INIT_RETRY_BASE_DELAY = 1000;

/** 이미지(img) resourceFetch span을 추적할 세션 비율(5%). 나머지 세션은 script만 추적. */
const IMAGE_TRACE_SAMPLE_RATE = 0.05;
/** 수동 오버라이드 키 — localStorage(영구) / URL 쿼리(set·clear) */
const IMAGE_TRACE_OVERRIDE_KEY = 'zep_trace_images';
const IMAGE_TRACE_QUERY_PARAM = 'zep_trace_images';
/** 세션 단위 샘플 결정 캐시 (sessionStorage) */
const IMAGE_TRACE_SESSION_KEY = 'zep_trace_images_session';

/**
 * Web Storage 접근을 호출별 try/catch로 감싼다.
 * 프라이빗 모드·쿼타 초과·샌드박스 iframe 등에서 `localStorage`/`sessionStorage`는
 * 접근(참조)만 해도 throw할 수 있어, 어떤 호출도 밖으로 예외를 던지지 않게 한다.
 */
type WebStorageName = 'local' | 'session';
const getWebStorage = (name: WebStorageName): Storage =>
  name === 'local' ? localStorage : sessionStorage;
const safeStorageGet = (name: WebStorageName, key: string): string | null => {
  try {
    return getWebStorage(name).getItem(key);
  } catch {
    return null;
  }
};
const safeStorageSet = (
  name: WebStorageName,
  key: string,
  value: string,
): void => {
  try {
    getWebStorage(name).setItem(key, value);
  } catch {
    /* 프라이빗 모드 / 쿼타 초과 등 → best-effort, 무시 */
  }
};
const safeStorageRemove = (name: WebStorageName, key: string): void => {
  try {
    getWebStorage(name).removeItem(key);
  } catch {
    /* noop */
  }
};

/**
 * HyperDX 브라우저 SDK를 래핑한 로거 클래스.
 * Race condition, 중복 초기화, 에러 처리 문제를 해결합니다.
 *
 * Features:
 * - State machine (idle → initializing → ready → failed)
 * - Call queueing (초기화 전 호출 저장)
 * - Bounded queue (최대 1000개)
 * - Bounded retry (최대 5회, exponential backoff)
 * - Dynamic import (SSR 안전성)
 * - Error handling (SDK 에러가 앱 크래시시키지 않음)
 */
type InitConfig = Partial<Omit<BrowserSDKConfig, 'apiKey' | 'url'>> & {
  mode?: HyperdxMode;
  service: string;
  workerFactory?: WorkerFactory;
  /** 초기화 최종 실패 또는 Worker 폴백 시 호출되는 콜백 (e.g. Sentry.captureException) */
  onInitFailed?: (error: unknown) => void;
};

export type HyperdxMode = 'worker';

class LoggerV2 {
  private hdx: HyperDXInstance | null = null;
  private state: 'idle' | 'initializing' | 'ready' | 'failed' = 'idle';
  private callQueue: Array<() => void> = [];

  // Worker 관련
  private mode: HyperdxMode | undefined;
  private workerClient: WorkerClient | null = null;
  private service = '';
  private debug = false;

  // Init retry 관련
  private initConfig: InitConfig | null = null;
  private initRetryCount = 0;

  static decideDisableReplay(disabledFromConfig?: boolean) {
    try {
      if (typeof disabledFromConfig === 'boolean') {
        sessionStorage.setItem(
          'zep_session_record',
          disabledFromConfig ? 'False' : 'True',
        );
        return disabledFromConfig;
      }

      // router 기반 이동이 아닌, fresh 페이지 이동 시에도 session 기록을 유지하기 위해
      // 세션스토리지에 세션 기록 유무를 기록합니다.
      // 만일 세션스토리지에 세션 기록 유무가 있으면, 세션 기록을 유지합니다.
      if (sessionStorage.getItem('zep_session_record') === 'True') {
        return false;
      } else {
        // 그렇지 않다면 세션 기록 유무를 새로 생성해줍니다
        // 40% 비율로 세션 기록
        const shouldRecordSession = Math.random() < 0.4;
        sessionStorage.setItem(
          'zep_session_record',
          shouldRecordSession ? 'True' : 'False',
        );
        return !shouldRecordSession;
      }
    } catch {
      // Private browsing 등에서 sessionStorage 접근 불가 시 replay 비활성화
      return true;
    }
  }

  /**
   * 이번 세션에서 이미지(img) resourceFetch span을 수집할지 결정한다.
   *
   * 기본은 세션의 5%(IMAGE_TRACE_SAMPLE_RATE)만 이미지를 추적해 볼륨을 ~95% 줄이면서
   * 대표 샘플로 이미지 로드 가시성은 유지한다(decideDisableReplay와 동일한 세션 단위 샘플링).
   * 샘플된 세션은 이미지 로드 시퀀스가 통째로 남아 디버깅에 유용하다.
   *
   * 수동 오버라이드(개발자가 임의로 켜기):
   * - URL `?zep_trace_images=1`(또는 `true`) → 켜고 localStorage에 저장(이후 세션도 유지)
   * - URL `?zep_trace_images=0`(또는 `false`) → 오버라이드 해제(다시 샘플링으로 복귀)
   * - localStorage `zep_trace_images = 'True'` → devtools에서 직접 켜기
   */
  static decideTraceImages(): boolean {
    if (typeof window === 'undefined') return false;

    // 1) URL 쿼리 오버라이드 (set / clear).
    //    storage 저장이 실패해도 명시적 의도(=1)는 그대로 반영한다.
    let qp: string | null = null;
    try {
      qp = new URLSearchParams(window.location.search).get(
        IMAGE_TRACE_QUERY_PARAM,
      );
    } catch {
      qp = null;
    }
    if (qp !== null) {
      if (qp === '1' || qp === 'true') {
        safeStorageSet('local', IMAGE_TRACE_OVERRIDE_KEY, 'True'); // best-effort 영구화
        return true;
      }
      safeStorageRemove('local', IMAGE_TRACE_OVERRIDE_KEY);
    }

    // 2) localStorage 수동 오버라이드
    if (safeStorageGet('local', IMAGE_TRACE_OVERRIDE_KEY) === 'True') {
      return true;
    }

    // 3) 세션 단위 5% 샘플 (sessionStorage로 세션 내 일관성 유지)
    const cached = safeStorageGet('session', IMAGE_TRACE_SESSION_KEY);
    if (cached === 'True') return true;
    if (cached === 'False') return false;

    const sampled = Math.random() < IMAGE_TRACE_SAMPLE_RATE;
    safeStorageSet(
      'session',
      IMAGE_TRACE_SESSION_KEY,
      sampled ? 'True' : 'False',
    );
    return sampled;
  }

  private getServiceName(serviceName: string) {
    // 서버 환경별 service name 생성
    // - DEV 환경: "dev-quiz-app", "dev-2-quiz-app" 등
    // 라이브는 앞에 프리픽스 존재하지않음
    if (process.env.NEXT_PUBLIC_STAGE === 'PRD') return serviceName;

    const serverName = process.env.NEXT_PUBLIC_SERVER_NAME;
    return serverName ? `${serverName}-${serviceName}` : serviceName;
  }

  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[HyperDX] ${message}`, ...args);
    }
  }

  /**
   * HyperDX SDK를 초기화합니다.
   * React StrictMode에서 중복 호출되어도 한 번만 실행됩니다.
   *
   * @param config - HyperDX 설정 객체 (BrowserSDKConfig)
   * - apiKey, url은 내부에서 고정시키기 위해 주입하는 config에서 제외합니다.
   * - mode: 'worker'이면 log()를 Worker로 전송 (메인 스레드 부하 감소)
   * @returns Promise<void> - 초기화 완료 또는 스킵 시 resolve
   *
   * @remarks
   * - SSR 환경(window undefined)에서는 실행되지 않습니다
   * - 이미 초기화 중이거나 완료되었으면 즉시 반환합니다
   * - 실패 시 최대 3회 exponential backoff로 재시도
   * - 모든 재시도 실패 후에만 state를 'failed'로 설정
   * - 재시도 중 callQueue는 유지됨 (비우지 않음)
   * - 성공 시 queue에 쌓인 모든 호출을 실행합니다
   */
  async init(config: InitConfig) {
    if (typeof window === 'undefined' || this.state !== 'idle') {
      return;
    }

    this.initConfig = config;
    this.initRetryCount = 0;
    await this.tryInit(config);
  }

  private async tryInit(config: InitConfig) {
    this.state = 'initializing';
    this.service = this.getServiceName(config.service);
    this.mode = config.mode;
    this.debug = config.debug || false;

    // Primary endpoint health check → 차단 시 fallback 사용
    const endpoint = await resolveEndpoint();
    this.debugLog(`Resolved endpoint: ${endpoint}`);

    if (this.mode === 'worker' && !this.workerClient) {
      if (!config.workerFactory) {
        console.error(
          "[HyperDX] workerFactory is required when mode is 'worker'",
        );
        this.mode = undefined;
      } else {
        try {
          this.workerClient = new WorkerClient({
            callbacks: {
              onError: (error, context) => {
                console.error('[HyperDX] Worker error:', error, context);
                this.recoverWorkerQueue();
              },
            },
            workerFactory: config.workerFactory,
          });
          const initialized = this.workerClient.init({
            service: this.service,
            endpoint,
            debug: config.debug,
          });
          if (!initialized) {
            this.mode = undefined;
            this.workerClient = null;
          }
        } catch (e) {
          console.error(
            '[HyperDX] Worker creation failed, falling back to SDK mode:',
            e,
          );
          this.mode = undefined;
          this.workerClient = null;
        }
      }
    }

    try {
      const { default: HyperDX } = await import('@hyperdx/browser');
      this.hdx = HyperDX;

      if (!this.hdx) {
        throw new Error('[HyperDX] Failed to load SDK');
      }

      // 이미지(img) resourceFetch span: 세션 5% 샘플 (+ 수동 오버라이드).
      // script 로드 추적은 항상 유지한다.
      const allowedInitiatorTypes = LoggerV2.decideTraceImages()
        ? ['img', 'script']
        : ['script'];

      this.hdx.init({
        // Hyperdx SDK 내부에서, api key가 없으면 rum이 제대로 생성되어 전달되지 않도록 설계하여
        // dummy api key를 설정합니다.
        // 우리의 경우 proxy server를 통해 로그를 수집하기 때문에 api key가 클라이언트에서는 필요하지 않습니다.
        apiKey: 'ZEP_API_KEY_RANDOM',
        url: endpoint,
        service: this.service ?? '',
        disableReplay: LoggerV2.decideDisableReplay(config.disableReplay),
        consoleCapture: false,
        tracePropagationTargets:
          (config && config.tracePropagationTargets) || [],
        // 외부(제3자) 네트워크 span 필터. 앱이 ignoreUrls를 명시하지 않으면 내부(zep)
        // 도메인 allowlist를 기본 적용해 구글/제타/광고 등 제3자 요청을 drop한다.
        // 참고: tracePropagationTargets는 trace 헤더 전파 대상일 뿐 로깅 필터가 아니다.
        ignoreUrls: config?.ignoreUrls ?? DEFAULT_EXTERNAL_IGNORE_URLS,
        maskAllInputs: true,
        maskAllText: true,

        // NOTE: 로그 이벤트 filtering 설정
        instrumentations: {
          interactions: false,
          visibility: false,
          // longtask: 무거운 JS 연산 등으로 인해 main thread가 50ms 이상 블로킹되었을 때 발생합니다.
          longtask: false,
          webvitals: false,
          document: false,
          // post-doc-load 리소스 instrumentation은 기본적으로 'img'/'script' 로드를
          // span으로 수집한다. play-app 같은 이미지 다수 앱에서는 맵 타일·스프라이트·
          // 아바타 png가 span의 최대 비중(약 32%)을 차지하므로, 세션의 5%만 'img'를
          // 추적하고(decideTraceImages) 나머지는 'script'만 남긴다. script(.js 청크)
          // 로드 추적은 로드 실패/지연 디버깅에 유용해 항상 유지한다.
          postload: { allowedInitiatorTypes },
          console: true,
          xhr: {
            applyCustomAttributesOnSpan: (span, _xhr) => {
              span.setAttribute('logLevel', ZLogLevel.INFO);
            },
          },
          fetch: {
            applyCustomAttributesOnSpan: (span, _fetch) => {
              span.setAttribute('logLevel', ZLogLevel.INFO);
            },
          },
        },
      });

      this.state = 'ready';
      this.initRetryCount = 0;

      // Flush queued calls
      this.callQueue.forEach(call => {
        try {
          call();
        } catch (e) {
          console.error('[HyperDX] Error executing queued call:', e);
        }
      });
      this.callQueue = [];
    } catch (e) {
      console.error('[HyperDX] Initialization failed:', e);
      this.hdx = null;

      if (this.initRetryCount < MAX_INIT_RETRIES) {
        const delay = INIT_RETRY_BASE_DELAY * Math.pow(2, this.initRetryCount);
        this.initRetryCount++;
        console.warn(
          `[HyperDX] Retrying init (${this.initRetryCount}/${MAX_INIT_RETRIES}) in ${delay}ms`,
        );
        this.state = 'idle';
        await new Promise(resolve => setTimeout(resolve, delay));
        await this.tryInit(config);
      } else {
        this.state = 'failed';
        this.callQueue = [];
        this.initConfig?.onInitFailed?.(e);
      }
    }
  }

  /**
   * Worker 에러 시 SDK 모드로 폴백하고, Worker 큐에 남은 로그 메시지를 SDK로 복구
   */
  private recoverWorkerQueue(): void {
    this.mode = undefined;
    this.initConfig?.onInitFailed?.(
      new Error('[HyperDX] Worker runtime error, falling back to SDK mode'),
    );

    if (!this.workerClient || !this.hdx) return;

    const queuedMessages = this.workerClient.getQueuedMessages();
    for (const msg of queuedMessages) {
      if (msg.type === 'log' && msg.entry) {
        try {
          this.hdx.addAction(msg.entry.event, msg.entry.attributes ?? {});
        } catch (e) {
          console.error('[HyperDX] Error recovering queued log:', e);
        }
      }
    }
  }

  /**
   * SDK가 ready 상태면 즉시 실행하고, 아니면 queue에 저장합니다.
   *
   * @param call - 실행할 함수
   *
   * @remarks
   * - state='ready'이고 hdx가 있으면: 즉시 실행 (try-catch로 보호)
   * - state='failed'면: 무시 (실행 안 함)
   * - 그 외(idle/initializing): queue에 추가
   * - Queue가 꽉 차면 가장 오래된 항목을 제거하고 경고 로그
   */
  private executeOrQueue(call: () => void) {
    if (this.state === 'ready' && this.hdx) {
      try {
        call();
      } catch (e) {
        console.error('[HyperDX] Error executing call:', e);
      }
    } else if (this.state !== 'failed') {
      if (this.callQueue.length >= MAX_QUEUE_LENGTH) {
        console.warn('[HyperDX] Queue full, dropping oldest call');
        this.callQueue.shift();
      }
      this.callQueue.push(call);
    }
  }

  /**
   * Worker로 로그 전송. 실패 시 호출부가 SDK 경로로 폴백할 수 있게 false를 반환합니다.
   */
  private sendLogToWorker(event: string, attributes?: Attributes): boolean {
    if (!this.workerClient) return false;
    const entry: LogEntry = {
      timestamp: Date.now(),
      type: 'log',
      event,
      attributes,
    };

    this.debugLog('Sending log to Worker:', event);
    return this.workerClient.post({ type: 'log', entry });
  }

  private isWorkerMode(): boolean {
    return this.mode === 'worker';
  }

  /**
   * 사용자 행동 이벤트를 로깅합니다.
   *
   * @param event - 이벤트 이름 (예: 'button_clicked', 'page_viewed')
   * @param attributes - 이벤트에 추가할 속성들 (선택사항)
   *
   * @example
   * ```typescript
   * hyperdx.log('purchase_completed', {
   *   orderId: '12345',
   *   amount: 99.99,
   *   currency: 'USD'
   * });
   * ```
   */
  log(
    event: string,
    attributes?: Attributes,
    level: ZLogLevel = ZLogLevel.INFO,
  ) {
    const startTime = loggerMetrics.startLogCall();
    const usedWorker = this.isWorkerMode() && !!this.workerClient;

    this.executeOrQueue(() => {
      const attrs: Attributes = {
        ...attributes,
        logLevel: level,
      };

      if (this.isWorkerMode() && this.sendLogToWorker(event, attrs)) {
        return;
      }

      this.hdx!.addAction(event, attrs);
    });

    loggerMetrics.endLogCall(startTime, usedWorker);
  }

  /**
   * Worker 모드와 무관하게 SDK 직접 경로로 이벤트를 기록합니다.
   * 통합 logger의 log/debug는 worker mode에서도 main thread 경로를 유지합니다.
   */
  logDirect(
    event: string,
    attributes?: Attributes,
    level: ZLogLevel = ZLogLevel.INFO,
  ) {
    const startTime = loggerMetrics.startLogCall();

    this.executeOrQueue(() => {
      this.hdx!.addAction(event, {
        ...attributes,
        logLevel: level,
      });
    });

    loggerMetrics.endLogCall(startTime, false);
  }

  /**
   * 예외/에러를 HyperDX에 기록합니다.
   *
   * @param error - 캡처할 Error 객체
   * @param attributes - 에러 컨텍스트 정보 (선택사항)
   *
   * @example
   * ```typescript
   * try {
   *   // some code
   * } catch (error) {
   *   hyperdx.captureException(error, {
   *     userId: '123',
   *     feature: 'checkout'
   *   });
   * }
   * ```
   */
  captureException(
    error: Error,
    attributes?: Attributes,
    level: ZLogLevel = ZLogLevel.ERROR,
  ) {
    this.executeOrQueue(() => {
      const hdx = this.hdx;
      if (hdx) {
        hdx.recordException(error, {
          ...attributes,
          logLevel: level,
        });
      }
    });
  }

  /**
   * 모든 이벤트에 포함될 글로벌 속성을 설정합니다.
   * 일반적으로 사용자 정보를 설정할 때 사용합니다.
   *
   * @param attributes - 설정할 글로벌 속성 객체
   *
   * @remarks
   * - 내부적으로 sanitizeAttrs를 사용하여 모든 값을 string으로 변환
   * - Session ID 획득을 최대 5회 재시도 (exponential backoff)
   *
   * @example
   * ```typescript
   * hyperdx.setGlobalAttributes({
   *   userId: '12345',
   *   userEmail: 'user@example.com',
   *   plan: 'premium'
   * });
   * ```
   */
  setGlobalAttributes(attributes: Attributes) {
    const sanitized = sanitizeAttrs(attributes);

    this.executeOrQueue(() => {
      const hdx = this.hdx;
      if (!hdx) return;

      const doSetGlobalAttributes = (attempt = 0): void => {
        const sessionId = hdx.getSessionId();
        if (sessionId) {
          const attrs = {
            ...sanitized,
            'session.id': sessionId,
          };

          // SDK에 설정
          hdx.setGlobalAttributes(attrs);

          // Worker 모드면 Worker에 globalAttributes 전달 (한번만)
          if (this.isWorkerMode() && this.workerClient) {
            this.workerClient.post({
              type: 'set_global_attributes',
              attributes: attrs,
            });
          }
        } else if (attempt < MAX_SESSION_RETRIES) {
          // 지수 백오프 적용
          const delay = 100 * Math.pow(2, attempt);
          setTimeout(() => doSetGlobalAttributes(attempt + 1), delay);
        } else {
          console.warn('[HyperDX] Session ID not available after max retries');
        }
      };

      doSetGlobalAttributes();
    });
  }

  /**
   * 벤치마크 실행
   * @param iterations 반복 횟수 (기본 100)
   */
  async benchmark(iterations = 100) {
    console.log(`[HyperDX] Running benchmark (${iterations} iterations)...`);
    const result = await LoggerMetrics.benchmark(
      (event, attrs) => this.log(event, attrs),
      iterations,
    );

    console.group('[HyperDX] Benchmark Results');
    console.log(`Mode: ${this.isWorkerMode() ? 'Worker' : 'SDK Direct'}`);
    console.log(`Iterations: ${iterations}`);
    console.log(`Avg: ${result.avg.toFixed(3)} ms`);
    console.log(`Min: ${result.min.toFixed(3)} ms`);
    console.log(`Max: ${result.max.toFixed(3)} ms`);
    console.log(`P95: ${result.p95.toFixed(3)} ms`);
    console.groupEnd();

    return result;
  }
}

/**
 * HyperDX 로거 싱글톤 인스턴스.
 *
 * @example
 * ```typescript
 * import { hyperdx } from '@zep/logger';
 *
 * // 초기화 (앱 시작 시 한 번)
 * hyperdx.init({
 *   service: 'my-app',
 *   debug: true
 * });
 *
 * // 이벤트 로깅
 * hyperdx.log('user_action', { action: 'click' });
 *
 * // 글로벌 속성 설정
 * hyperdx.setGlobalAttributes({
 *   userId: '123',
 *   userEmail: 'user@example.com'
 * });
 * ```
 */
export const hyperdx = new LoggerV2();
